/**
 * Type definitions for Fixed Routes integration into Atlas
 * Adapted from CirculatorWebpage types to work with Atlas's Layer, Line, and Point types
 */

import type { Point, Line } from '../context/context';

/**
 * Represents a fixed route stop location
 * Adapted from CirculatorWebpage Stop type
 */
export interface FixedRouteStop {
  id: string;
  name: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  /**
   * Reference to the Atlas map point ID if this stop was created from a map point
   * If set, the stop may optionally sync with the map point's coordinates
   */
  mapPointId?: string;
  /**
   * UAT stop ID - preserved when importing stops from UAT
   * Used to map Atlas stop IDs to UAT stop IDs during upload
   */
  uatStopId?: string;
  /**
   * Label from UAT (alternative name/identifier)
   */
  label?: string;
  /**
   * Address of the stop (from UAT import)
   */
  address?: string;
  /**
   * Description of the stop (from UAT import)
   */
  description?: string;
  /**
   * Google Places ID (from UAT import)
   */
  placeId?: string;
  /**
   * Original UAT stop data - preserved for future reference and re-export
   */
  uatMetadata?: {
    originalId: string;
    originalName?: string;
    originalLabel?: string;
    importedAt?: number; // Timestamp when imported
    importedFrom?: string; // Environment (UAT/Production)
    productId?: string; // Product/network ID
  };
  /**
   * Caller-defined external stop reference id (e.g. a postcode like "SO31 4RA",
   * a resolved bus stop / hub name like "Lake Road Health Centre", or a UAT
   * stop id). Threaded from the Postcode Stop Assignment tool via the ride's
   * `external_stop_reference_id` so the Fixed Route export can build a
   * human-readable stop label instead of falling back to coordinates.
   *
   * Optional — only populated when a `FixedRouteStop` was built from a
   * simulation activity whose underlying ride carried the field.
   */
  externalStopReferenceId?: string;
  /**
   * NaPTAN ATCO code — the UK national identifier for a public-transport access
   * point (stop). TransXChange / BODS reference stops by AtcoCode, so this is
   * preserved on BODS / TransXChange **ingest** and written back on BODS
   * **export**, giving a full-fidelity round-trip for any BODS-origin stop. When
   * absent (e.g. a stop drawn from scratch), BODS export falls back to a
   * lat/lng `AnnotatedStopPointRef` and flags the stop as not NaPTAN-matched.
   * See `FeaturePlans/BodsIntegration/prd-00-master-index.md` §5 #2.
   */
  atcoCode?: string;
  /**
   * NaPTAN code — the short, public-facing stop code (printed on bus-stop flags /
   * used by SMS departure services). Optional companion to `atcoCode`, carried
   * through from NaPTAN / BODS data when available.
   */
  naptanCode?: string;
  /**
   * Metadata for simulation-results imports.
   */
  simulationImport?: {
    runId: string;
    shiftId: string;
    stopSequence: number;
    pickupCount: number;
    dropoffCount: number;
    pickedUpRiderIds: string[];
    droppedOffRiderIds: string[];
  };
}

/**
 * Represents a travel segment between two consecutive stops
 * Adapted from CirculatorWebpage RouteSegment type
 */
export interface FixedRouteSegment {
  fromStopId: string;
  toStopId: string;
  travelTimeMinutes: number; // Base travel time (before multiplier)
  distanceMeters?: number; // Distance in meters (optional for backward compatibility)
}

/**
 * Configuration for a stop within a route (includes wait time)
 * Adapted from CirculatorWebpage StopConfig type
 */
export interface FixedRouteStopConfig {
  stopId: string;
  waitTimeMinutes: number;
}

/**
 * Route geometry stored as coordinate sequences from routing service
 * This represents the actual road path between stops, not straight-line connections
 */
export interface RouteGeometry {
  /**
   * Full coordinate sequence for the route following roads
   * Format: [[lat1, lng1], [lat2, lng2], ...]
   * Compatible with Atlas Line.coordinates format
   */
  coordinates: [number, number][];
  /**
   * Timestamp when geometry was calculated
   * Used for cache invalidation
   */
  calculatedAt: number;
  /**
   * Hash of stop IDs and order used to calculate this geometry
   * Used to detect if geometry needs recalculation
   */
  stopsHash: string;
  /**
   * Optional per-stop-pair segment geometries (index i = stop_i -> stop_{i+1}).
   * Used to support incremental geometry updates without re-routing unchanged legs.
   */
  segmentGeometries?: [number, number][][];
  /**
   * Optional return leg geometry for circulator routes (last stop -> first stop).
   */
  returnSegmentGeometry?: [number, number][];
  /**
   * Optional effective waypoint chain used for routed geometry generation.
   * Includes stop anchors and interleaved control waypoints.
   */
  waypointChain?: [number, number][];
  /**
   * Optional leg geometries for waypointChain.
   * Index i represents routed geometry from waypointChain[i] to waypointChain[i + 1].
   */
  waypointLegGeometries?: [number, number][][];
  /**
   * Optional repaired waypoint segment anchors inferred from fallback chain assignment.
   * Persisted to stabilize future waypoint edits.
   */
  repairedWaypointStopIndices?: number[];
}

/**
 * Day-of-week service mask (Mon–Sun) — mirrors the GTFS `calendar.txt` booleans.
 * Used for both the route-level service calendar and per-vehicle assignment
 * overrides in the draft operation-assignment buffer (§7.1).
 */
export interface ServiceDaysMask {
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

/**
 * GTFS calendar-date exception kind: an explicitly added or removed service date.
 * Maps from GTFS `calendar_dates.txt` `exception_type` (`'1'` -> 'added',
 * `'2'` -> 'removed').
 */
export type ServiceExceptionType = 'added' | 'removed';

/**
 * Operating-cost rates for a fixed route. A simplified two-and-a-half-variable
 * reduction of the classic transit three-variable cost model
 * (`Cost = c_H·hours + c_K·km + c_V·vehicles`):
 *  - `dailyFixedCostPerVehicle` stands in for the per-vehicle capital/overhead term
 *    (`c_V`), charged once per vehicle per day of operation;
 *  - `costPerKm` is the distance/variable term (`c_K`);
 *  - `hourlyWage` is the labour term (`c_H`), charged per vehicle service-hour.
 * All values are in the {@link NetworkCostSettings.currency}; no FX conversion is done.
 */
export interface CostRates {
  /** Fixed cost incurred per vehicle per day the route operates. */
  dailyFixedCostPerVehicle: number;
  /** Variable cost per kilometre travelled. */
  costPerKm: number;
  /** Labour cost per vehicle service-hour. */
  hourlyWage: number;
}

/**
 * Network-level default cost rates + display currency. A planner sets these once
 * for the whole network; any route may override individual rates via
 * {@link FixedRoute.costOverride}. Persisted locally (v1) — see the cost-calculation PRD.
 */
export interface NetworkCostSettings extends CostRates {
  /** ISO 4217 currency code used to format all cost figures (e.g. `'USD'`, `'GBP'`). */
  currency: string;
}

/** Sensible default network cost settings (USD; rates align with the DRT fleet calculator). */
export const DEFAULT_COST_SETTINGS: NetworkCostSettings = {
  currency: 'USD',
  dailyFixedCostPerVehicle: 1000,
  costPerKm: 1,
  hourlyWage: 30,
};

/**
 * GTFS `agency.txt` record, captured at import and preserved per-route so a
 * re-export reproduces the original agency rather than a synthetic default.
 * Stored denormalised on each {@link FixedRoute}; the GTFS exporter dedupes by
 * `agencyId` when writing `agency.txt`, so multi-agency feeds round-trip
 * correctly. `timezone` is an IANA zone (e.g. `Australia/Perth`).
 */
export interface GtfsAgency {
  agencyId: string;
  name: string;
  url: string;
  timezone: string;
  lang?: string;
}

/**
 * A single draft vehicle assignment within a {@link RouteOperationAssignment}.
 *
 * Local-only staging shape (§7.1). Serialises into the existing
 * `RouteShiftIntent` / `DatedShiftSolutionRequest` at deploy time — see
 * `fixedRouteUatClient.ts`. `direction` and `shiftType` use the Hailer-aligned
 * unions ('forward' | 'reverse' and 'timetable' | 'shuttle').
 */
export interface DraftVehicleAssignment {
  /** Ops vehicle UUID (`opsVehicleId` from `getvehicles`). */
  opsVehicleId: string;
  /**
   * Human-readable Ops vehicle label (rego + seating, e.g. "ABC345 (60 std / 1
   * wc)") resolved from the loaded vehicle list at assign time, persisted so the
   * NAME — not just the id — is shown everywhere the plan is read back (route
   * details, Atlas-routes card, upload modal, saved maps). Display only;
   * not sent to Ops (the id is authoritative).
   */
  opsVehicleLabel?: string;
  /** Back-reference to the local Atlas vehicle id, for the mapping. */
  cartographerVehicleId?: string;
  /** Ops driver id, if assigned (driver picker is a later phase). */
  driverId?: string;
  /** Hailer direction: 'forward' | 'reverse'. */
  direction: 'forward' | 'reverse';
  /** Index of the stop at which this vehicle starts the route. */
  startStopIndex: number;
  /** Offset (minutes) of the first stop departure from the shift start. */
  firstStopOffsetMins: number;
  /** Number of repeats (loops) within the window. */
  numberOfRepeats: number;
  /** Hailer shift type: 'timetable' | 'shuttle'. */
  shiftType: 'timetable' | 'shuttle';
  /** Window start (HH:mm or ISO). */
  startTime: string;
  /** Window end (HH:mm or ISO). */
  endTime: string;
  /** Optional per-vehicle day-of-week override of the route service days. */
  serviceDays?: ServiceDaysMask;
}

/**
 * A draft assignment of one operation solution to a route, with the vehicles
 * (and their day/time windows) that should run it. Local-only staging buffer;
 * source of truth remains Ops shift solutions (§2.4 / §7.1).
 */
export interface RouteOperationAssignment {
  operationSolutionId: string;
  operationName?: string;
  vehicleAssignments: DraftVehicleAssignment[];
  /**
   * Plan-level date range applied to ALL vehicles/shifts in this operation
   * (set in the Attach Vehicles & Operations modal). Composed into each shift
   * solution's `startDate`/`endDate`. ISO `YYYY-MM-DD`. When absent, the preview
   * falls back to the route's GTFS `serviceDateRange`.
   */
  dateRange?: { startDate: string; endDate: string };
}

/**
 * Represents a complete fixed route configuration
 * Adapted from CirculatorWebpage Route type with Atlas integration
 */
export interface FixedRoute {
  id: string;
  name: string;
  /**
   * Short name/subtitle for the route (e.g., route number from GTFS route_short_name)
   * Displayed below the main route name when available
   */
  shortName?: string;
  startTime: string; // HH:mm format (e.g., "08:00")
  endTime?: string; // HH:mm format (e.g., "22:00") - optional, defaults to global operating hours end
  stops: FixedRouteStopConfig[]; // Ordered list of stops with wait times
  segments: FixedRouteSegment[]; // Travel times between consecutive stops
  trafficMultiplier?: number; // Per-route traffic multiplier (default: 1.0)
  
  /**
   * Route geometry following actual roads
   * Calculated via routing service (OSRM)
   */
  geometry?: RouteGeometry;
  
  /**
   * Waypoints for manual route editing (Google Maps-like)
   * User-adjustable control points that shape the route path
   */
  waypoints?: [number, number][];

  /**
   * Optional segment index anchors for waypoints.
   * Each entry corresponds to `waypoints[i]` and indicates the stop-segment index
   * the waypoint belongs to (between stops[index] and stops[index + 1]).
   * Used to preserve click-order intent (e.g. stop1 -> stop2 -> waypoint -> stop3).
   */
  waypointStopIndices?: number[];
  
  /**
   * Distinct color for this route (auto-assigned or user-configurable)
   * Used for rendering on map
   */
  color?: string;
  
  /**
   * Line width for this route (in pixels)
   * Used for rendering on map
   */
  width?: number;
  
  /**
   * Whether this route is a circulator (repeating route)
   */
  isCirculator?: boolean;

  /**
   * For non-circulator routes, publish the return journey as the same stop
   * sequence in reverse instead of treating the return as deadhead time.
   */
  reverseReturnJourney?: boolean;
  
  /**
   * Number of evenly spaced vehicles for circulator routes
   * Only applicable when isCirculator === true
   * Used to calculate headway (time between vehicles) and frequency (vehicles per hour)
   */
  vehicleCount?: number;
  
  /**
   * Vehicle capacity (passengers per vehicle)
   * Used to calculate daily and hourly capacity heuristics
   * Defaults to a standard capacity if not specified
   */
  vehicleCapacity?: number;
  
  /**
   * Whether this route uses bus routing (limits to roads buses can travel on)
   * When enabled, uses Mapbox driving-traffic profile optimized for bus routing
   * Note: Mapbox doesn't have a dedicated bus profile, so this uses traffic-aware routing
   * which is appropriate for buses but may not exclude all roads unsuitable for buses
   */
  isBusRoute?: boolean;
  
  /**
   * Visibility state for this route
   */
  hidden?: boolean;
  
  /**
   * Whether this route's geometry has been manually edited
   * When true, prevents automatic geometry recalculation from stop changes
   */
  isManuallyEdited?: boolean;

  /**
   * Freehand mode: the route alignment is drawn as literal straight lines between stops
   * (no road-snapping of captured points, no road-following geometry). Useful when the road
   * network is wrong/missing or the planner wants an exact shape. Defaults to false (snap-to-road).
   */
  freehand?: boolean;

  /**
   * Direction of this route within a bidirectional pair. Real fixed routes typically have an
   * outbound and an inbound pattern (which may differ). Set when a return route is created from
   * an outbound one. Absent for single-direction routes (backward compatible).
   */
  directionId?: 'outbound' | 'inbound';

  /**
   * Id of the paired opposite-direction route (outbound ↔ inbound), if this route is part of a
   * bidirectional pair. Lets the UI surface / navigate between the two directions.
   */
  linkedRouteId?: string;
  
  /**
   * Reference to the Atlas Line ID if this route has been rendered on the map
   */
  lineId?: string;
  
  /**
   * UAT route ID - preserved when importing routes from UAT
   * Used to map Atlas route IDs to UAT route IDs during upload
   */
  uatRouteId?: string;
  
  /**
   * Label from UAT (alternative name/identifier)
   */
  label?: string;
  
  /**
   * Original UAT route data - preserved for future reference and re-export
   */
  uatMetadata?: {
    originalId: string;
    originalName?: string;
    originalLabel?: string;
    importedAt?: number; // Timestamp when imported
    importedFrom?: string; // Environment (UAT/Production)
    productId?: string; // Product/network ID
    serviceDate?: string; // Service date when imported
    opsVehicleId?: string; // Ops vehicle UUID preserved for shift assignment
    opsVehicleLabel?: string; // Human-readable Ops vehicle label
  };
  
  /**
   * Reference to the Atlas Layer ID containing this route
   */
  layerId?: number;

  /**
   * Original GTFS route_id (for grouping multiple trips of the same route)
   */
  gtfsRouteId?: string;

  /**
   * GTFS `route_type` captured at import (0=Tram, 1=Subway, 2=Rail, 3=Bus,
   * 4=Ferry, …). Preserved so a re-export reproduces the original mode.
   * Absent for Atlas-authored routes, which export as Bus (3).
   */
  gtfsRouteType?: number;

  /**
   * GTFS `route_desc` free-text description (routes.txt). Editable per-route in
   * the Route Details tab; preserved across import/export.
   */
  routeDesc?: string;

  /**
   * GTFS `route_text_color` (hex, with or without leading `#`). Preserved from
   * import; paired with `color` (route_color) on export.
   */
  routeTextColor?: string;

  /**
   * GTFS `trip_headsign` for this route's trips. Editable per-route in Route
   * Details; on export, falls back to the last stop's name when unset.
   */
  tripHeadsign?: string;

  /**
   * GTFS `agency.txt` record this route belongs to, captured at import and
   * preserved for round-trip export. Absent for authored routes (which use the
   * export modal's default agency). See {@link GtfsAgency}.
   */
  gtfsAgency?: GtfsAgency;

  /**
   * Trip schedules for routes with multiple runs per day (e.g., GTFS routes)
   * Each trip schedule contains arrival times at each stop for a specific trip/run
   * Format: Array of trip schedules, each with:
   *   - startTime: Trip start time in minutes from midnight (0-1439)
   *   - stopArrivalTimes: Array of arrival times in minutes from trip start, one per stop
   * 
   * This allows FRODO to efficiently find the next available trip for any request time
   * For circulator routes, this is not needed (loops are calculated dynamically)
   * For non-circulator routes with multiple runs, this enables checking all runs
   */
  tripSchedules?: Array<{
    startTimeMinutes: number; // Trip start time (0-1439, or can exceed for overnight)
    stopArrivalTimesMinutes: number[]; // Arrival time at each stop, in minutes from trip start
  }>;

  /**
   * GTFS service calendar (Mon–Sun booleans) for the selected service_id.
   * Parsed from GTFS `calendar.txt` at import (`'1'` -> true). Optional — left
   * undefined when no calendar is present in the feed.
   * Phase 1 draft/staging buffer only (§7.1); the source of truth for live
   * scheduling is Ops shift solutions.
   */
  serviceDays?: ServiceDaysMask;

  /**
   * Service operating date range (ISO `YYYY-MM-DD`) for the selected service_id,
   * from GTFS `calendar.txt` `start_date`/`end_date`. Optional.
   */
  serviceDateRange?: { startDate: string; endDate: string };

  /**
   * Service calendar exceptions from GTFS `calendar_dates.txt` for the selected
   * service_id. `exception_type` `'1'` -> `'added'`, `'2'` -> `'removed'`.
   * Optional — left undefined when no exceptions are present.
   */
  serviceExceptions?: Array<{ date: string; type: ServiceExceptionType }>;

  /**
   * DRAFT operation/vehicle/day assignment staging buffer (§7.1).
   *
   * Local-only, session-scoped draft of "this route runs with these vehicles on
   * these days/times" that the compose UI builds before deploy. This is NOT a
   * source of truth — the authoritative record is Ops shift solutions, read back
   * via `getoperationssolutionsall`. Persisted via the existing `updateRoute()`.
   * Optional and additive; existing routes are unaffected.
   */
  operationAssignments?: RouteOperationAssignment[];

  /**
   * Provenance for a route **forked from a hosted (live) GTFS network** for editing
   * (PRD-04 FR15/FR16). Its presence is the live-vs-edited discriminator: a route with
   * `networkOrigin` is an *editable copy* derived from a read-only live feed route — the
   * live reference itself is never mutated (it lives in the Network tab, not in `routes`).
   * Traces the copy back to `{feedId}@{version}#{gtfsRouteId}`.
   */
  networkOrigin?: {
    feedId: string;
    version?: string;
    gtfsRouteId: string;
    forkedAt?: number; // epoch ms when the copy was created
  };

  /**
   * Per-route operating-cost override. Any field set here takes precedence over the
   * network-default {@link NetworkCostSettings}; unset fields inherit the default. Use for a
   * route that runs a pricier/cheaper vehicle than the network norm. Persisted with the route.
   */
  costOverride?: Partial<CostRates>;

  /**
   * Metadata for simulation-results imports.
   */
  simulationImport?: {
    runId: string;
    shiftId: string;
    source: 'simulation_results';
    networkTag?: string;
    networkLayerName?: string;
    shiftBand?: 'DAY' | 'LATE' | 'NIGHT';
    stableName?: string;
    fleetName?: string;
    manifestName?: string;
    opsVehicleId?: string;
    opsVehicleLabel?: string;
  };
}

/**
 * Route assignment metadata for vehicle-to-route assignments
 * Used in fleet generation to track which routes a vehicle serves
 */
export interface RouteAssignment {
  routeId: string;
  routeName: string;
  loopNumbers?: number[]; // For circulators, which loops this vehicle serves
  startStop?: string; // Stop ID where vehicle starts serving route
  endStop?: string; // Stop ID where vehicle ends serving route
}

/**
 * Complete fixed routes state
 * Contains all stops and routes for the Fixed Routes system
 */
export interface FixedRoutesState {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  trafficMultiplier: number; // Global traffic multiplier (default: 1.0)
}

/**
 * Export/Import data structure for Fixed Routes
 */
export interface FixedRoutesExportData {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  trafficMultiplier: number;
  exportDate: string; // ISO date string
}

/**
 * Metadata stored in Atlas Line features to identify them as fixed routes
 * This allows us to link Atlas Lines back to FixedRoute objects
 */
export interface FixedRouteLineMetadata {
  /**
   * Type identifier to distinguish fixed route lines from regular lines
   */
  type: 'fixedRoute';
  /**
   * Reference to the FixedRoute ID
   */
  fixedRouteId: string;
  /**
   * Route name for display purposes
   */
  routeName: string;
  /**
   * Short name/subtitle for the route (e.g., route number from GTFS route_short_name)
   * Displayed below the main route name when available
   */
  shortName?: string;
  /**
   * Whether this route is a circulator
   */
  isCirculator?: boolean;
  /** Layer name for this route network (optional override). */
  networkLayerName?: string;
}

/**
 * Helper type: Atlas Line with Fixed Route metadata
 */
export type FixedRouteLine = Line & {
  fixedRouteMetadata?: FixedRouteLineMetadata;
};

/**
 * Metadata stored in Atlas Point features to identify them as fixed route stops
 */
export interface FixedRouteStopPointMetadata {
  /**
   * Type identifier to distinguish fixed route stop points from regular points
   */
  type: 'fixedRouteStop';
  /**
   * Reference to the FixedRouteStop ID
   */
  fixedRouteStopId: string;
  /**
   * UAT stop ID - preserved when importing stops from UAT
   */
  uatStopId?: string;
  /**
   * Label from UAT (alternative name/identifier)
   */
  label?: string;
  /**
   * Address of the stop
   */
  address?: string;
  /**
   * Google Places ID
   */
  placeId?: string;
  /**
   * Original UAT stop metadata
   */
  uatMetadata?: {
    originalId: string;
    originalName?: string;
    originalLabel?: string;
    importedAt?: number;
    importedFrom?: string;
    productId?: string;
  };
}

/**
 * Helper type: Atlas Point that may be used as a Fixed Route stop
 */
export type PointWithStopReference = Point & {
  fixedRouteStopId?: string;
  fixedRouteStopMetadata?: FixedRouteStopPointMetadata;
};

/**
 * Conversion utilities type helpers
 */
export interface RouteConversionResult {
  line: FixedRouteLine;
  stopPoints: Point[];
}

/**
 * Type guard to check if a Line is a Fixed Route line
 */
export function isFixedRouteLine(line: Line): line is FixedRouteLine {
  return (line as any).fixedRouteMetadata?.type === 'fixedRoute';
}

/**
 * Type guard to check if a Point has a Fixed Route stop reference
 */
export function isPointWithStopReference(point: Point): point is PointWithStopReference {
  return 'fixedRouteStopId' in point || 'fixedRouteStopMetadata' in point;
}

